// Putting plain text on the clipboard, with a seam a test can hold (T-135; REQ-104 AC5).
//
// **Why this is a module and not two lines at the call site.** `navigator.clipboard` does not exist in
// jsdom, is undefined on an insecure origin, and throws rather than resolving when the document is not
// focused — three ways for a button that says "Copied." to be lying. So every copy in this app goes
// through one function that **reports whether it worked**, and every caller says something different when
// it did not (REQ-287 AC3: never a success message for a failure).
//
// The fallback is the old `execCommand('copy')` path, which still works where the async API is blocked. It
// is deliberately the second choice: it moves the selection and only works inside a user gesture.
//
// Nothing here formats anything. The checklist's text is `renderChecklist` from `@sl/core/messages`
// (T-127's note (4)) and an entity's name is the entity's name.

/** Swapped in tests (`setClipboardWriter`); `null` means "use the platform". */
let writer: ((text: string) => Promise<void>) | null = null;

/** A test seam, and the only one — production code never calls it. */
export function setClipboardWriter(next: ((text: string) => Promise<void>) | null): void {
  writer = next;
}

/** True when the text really reached the clipboard. Never throws: the caller decides what to say. */
export async function copyText(text: string): Promise<boolean> {
  if (writer) {
    try {
      await writer(text);
      return true;
    } catch {
      return false;
    }
  }
  const api = globalThis.navigator?.clipboard;
  if (api && typeof api.writeText === 'function') {
    try {
      await api.writeText(text);
      return true;
    } catch {
      // Blocked, or the document is not focused — fall through to the older path rather than give up.
    }
  }
  return legacyCopy(text);
}

/** `execCommand('copy')` over a hidden textarea; the selection is put back where it was. */
function legacyCopy(text: string): boolean {
  if (typeof document === 'undefined' || typeof document.execCommand !== 'function') return false;
  const field = document.createElement('textarea');
  field.value = text;
  // Off-screen rather than `display: none`: a hidden field cannot be selected.
  field.setAttribute('readonly', '');
  field.style.position = 'fixed';
  field.style.top = '-9999px';
  field.style.opacity = '0';
  document.body.appendChild(field);
  const previous = document.getSelection()?.rangeCount ? document.getSelection()!.getRangeAt(0) : null; // prettier-ignore
  try {
    field.select();
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    field.remove();
    if (previous) {
      const selection = document.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(previous);
    }
  }
}
